import videojs from 'videojs-mux-kit';
import PortalIdParser from 'PortalIdParser';
import { PlayerTypes } from 'video-embed/types';
import { getMuxEnvKey } from './env';
import { setupTranslations } from './I18n';
import './playbackOptions';
import './progressDuration';
import './AutoplayToggle';
import '../plugins/errorPlugin';
import '../plugins/captions';
import '../plugins/seekBar';
import { getInitialLevel } from './networkObserver';
export const HLS_LAZY_LOAD_DELAY = 8000;
export function getHlsTech(player) {
  const tech = player.tech('Html5');
  if (tech && tech.hlsjs) {
    return tech.hlsjs;
  }
  return null;
}
const CONTROL_BAR_CHILDREN_WITH_CHAPTERS = ['PlayToggle', 'ProgressControl', 'ProgressDuration', 'AutoplayToggle', 'VolumePanel', 'CaptionsButton', 'PlaybackPopover', 'PictureInPictureToggle', 'FullscreenToggle'];
export const getBasePlayerConfig = (video, options, trackingProps = {}) => {
  const isAcademyVideo = options.playerType === PlayerTypes.ACADEMY_IN_APP || options.playerType === PlayerTypes.ACADEMY_EMBEDDED;
  const isCompactVideo = options.playerType === PlayerTypes.COMPACT_PLAYER || options.playerType === PlayerTypes.COMPACT_PLAYER_WITH_CUSTOM_MENU;
  const academyPlayerConfig = isAcademyVideo ? {
    capLevelToPlayerSize: true
  } : isCompactVideo ? {
    startLevel: 0,
    capLevelToPlayerSize: true
  } : {};
  const config = {
    html5: {
      hls: Object.assign({
        // This is used to set the initial quality of the video, more details in https://github.com/video-dev/hls.js/blob/master/docs/API.md#startlevel
        startLevel: getInitialLevel()
      }, academyPlayerConfig, {
        // debug: true,
        // we call `hls.startLoad(-1)` explicitly so lazy loading is possible
        autoStartLoad: false,
        // max MB of buffer (default 60), see https://github.com/video-dev/hls.js/blob/master/docs/API.md
        maxBufferSize: 20,
        // max seconds of buffering (default 30s)
        maxBufferLength: 15
      })
    },
    plugins: {
      mux: {
        disableCookies: true,
        data: {
          env_key: getMuxEnvKey(),
          player_init_time: Date.now(),
          player_name: trackingProps.renderContext || 'video-player-ui',
          player_autoplay: Boolean(options.autoplay),
          player_preload: false,
          video_id: video.videoId,
          video_title: video.title,
          video_producer: PortalIdParser.get(),
          sub_property_id: PortalIdParser.get(),
          page_url: trackingProps.pageUrl
        }
      }
    },
    controlBar: {
      children: CONTROL_BAR_CHILDREN_WITH_CHAPTERS,
      VolumePanel: {
        inline: false
      },
      PictureInPictureToggle: Boolean(options.enablePictureInPicture),
      AutoplayToggle: Boolean(options.enableAutoPlayToggle)
    },
    fill: true,
    timelineHoverPreviews: true,
    userActions: {
      doubleClick: false
    },
    textTrackSettings: false
  };

  // Mux Data works for raw FM file urls as well in which case `video_cdn` will be missing, this allows for easy filtering
  if (!video.primaryPlaybackId) {
    config.plugins.mux.data.video_cdn = 'hs-files';
  }
  return config;
};
export default function createPlayer(idOrElement, options, video, trackingProps) {
  var _player$seekBar, _video$chapters, _player$captions;
  const posterUrl = options.posterUrl || video.posterUrl;
  const source = video.primaryPlaybackId ? {
    type: 'video/mux',
    src: video.primaryPlaybackId
  } : {
    src: video.mp4FileUrl || video.fileUrl
  };
  if (options.locale) {
    videojs.addLanguage(options.locale, setupTranslations());
  }
  const player = videojs(idOrElement, Object.assign({}, getBasePlayerConfig(video, options, trackingProps), {
    sources: [source],
    poster: options.hidePosterImage ? undefined : posterUrl,
    controls: !options.hiddenControls,
    loop: options.loop,
    muted: options.muted,
    language: options.locale
  }));
  if (options.playbackRate && options.playbackRate !== 1) {
    player.ready(() => player.playbackRate(options.playbackRate));
  }
  (_player$seekBar = player.seekBar) === null || _player$seekBar === void 0 || _player$seekBar.call(player, {
    chapters: (_video$chapters = video.chapters) === null || _video$chapters === void 0 ? void 0 : _video$chapters.items,
    duration: video.duration,
    playButtonColor: options.playButtonColor,
    primaryPlaybackId: video.primaryPlaybackId || undefined
  });
  (_player$captions = player.captions) === null || _player$captions === void 0 || _player$captions.call(player);
  return player;
}